import { API } from '../config';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';


export function NotificationBell({ wsMessage }) {
  const { getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: getAuthHeaders() }),
        axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeaders() })
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch {}
  };

  useEffect(() => { fetchNotifications(); }, []); // eslint-disable-line

  useEffect(() => {
    if (wsMessage?.type === 'notification') {
      const notifId = `${wsMessage.conversation_id}_${Date.now()}`;
      setUnreadCount(prev => prev + 1);
      setNotifications(prev => {
        // Deduplicate: skip if same conversation notif within 2 seconds
        const recent = prev[0];
        if (recent && recent.conversation_id === wsMessage.conversation_id && Date.now() - new Date(recent.timestamp).getTime() < 2000) return prev;
        return [{ id: notifId, text: wsMessage.text, read: false, timestamp: new Date().toISOString(), conversation_id: wsMessage.conversation_id }, ...prev.slice(0, 49)];
      });
    }
  }, [wsMessage]);

  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/mark-read`, {}, { headers: getAuthHeaders() }); setUnreadCount(0); setNotifications(prev => prev.map(n => ({ ...n, read: true }))); } catch {}
  };

  const formatTime = (ts) => {
    if (!ts) return ''; const d = new Date(ts); const diff = Date.now() - d;
    if (diff < 60000) return 'Az önce'; if (diff < 3600000) return `${Math.floor(diff / 60000)}dk önce`; if (diff < 86400000) return `${Math.floor(diff / 3600000)}sa önce`; return d.toLocaleDateString('tr-TR');
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="notifications-button" variant="ghost" size="icon" className="relative rounded-xl">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && <Badge data-testid="notifications-badge" className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center text-xs px-1 bg-destructive text-destructive-foreground border-2 border-background rounded-full">{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 rounded-xl" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/70">
          <h3 className="font-semibold text-sm">Bildirimler</h3>
          {unreadCount > 0 && <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead}><Check className="w-3 h-3 mr-1" /> Tümünü Oku</Button>}
        </div>
        <ScrollArea className="max-h-64">
          {notifications.length === 0 ? (<div className="p-6 text-center text-muted-foreground text-sm">Henüz bildirim yok</div>) : (
            <div className="divide-y divide-border/50">{notifications.slice(0, 20).map(n => (
              <div key={n.id} className={`px-4 py-3 text-sm ${!n.read ? 'bg-primary/5' : ''}`}>
                <p className={!n.read ? 'font-medium' : 'text-muted-foreground'}>{n.text}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatTime(n.timestamp)}</p>
              </div>
            ))}</div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
